import { useQuery, useMutation } from '@apollo/react-hooks';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useContext } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import StepHeader from 'components/StepHeader';
import Toaster from 'components/Toaster';
import { GET_ACCOUNTS } from 'pages/app/Credits/gql/queries/get-accounts';
import { query as GET_USER } from 'pages/app/Users/gql/queries/get-user';
import { AppContext } from 'shared/AppContext';
import { formatPhoneNumber } from 'shared/formatters';
import GetCustomer from '../../gql/get-customer';
import GetUserOpenCreditsTotal from '../../gql/get-user-open-credits-total';
import CREATE_CREDIT_V2 from '../../gql/mutations/create-credit-v2';
import {
  FooterButtons,
  NextButton,
  SendButton,
} from '../SendCampaign/StyledComponents';
import Step1CreditDetails from './Step1CreditDetails';
import Step2MessageComposer from './Step2MessageComposer';
import Step3Review from './Step3Review';
import {
  PageContainer,
  PageContent,
  PageTitle,
  spin,
} from './StyledComponents';

const SpinnerIcon = styled.svg`
  animation: ${spin} 1s linear infinite;
  margin-right: 8px;
`;

// Helper to get the app name based on platform
const getAppName = () =>
  process.env.REACT_APP_PLATFORM === 'tickethoss' ? 'Ticket Hoss' : 'Pit Pay';

// Helper function to get text based on adjustment type
const getAdjustmentText = (type, key) => {
  const texts = {
    credit: {
      title: 'Issue Credit',
      pageTitle: 'Issue Credit',
      amount: 'Credit Amount',
      button: 'Issue Credit',
      sendButton: 'Send Credit',
      success: 'Credit Issued Successfully',
      successMessage: 'Credit has been issued to',
      message: 'has issued you a {{credit_amount}} credit',
      messageTitle: 'You received a credit!',
      currentBalance: 'Current Credit Balance',
      step1Title: 'Credit Details',
      step3Title: 'Review Credit',
    },
    refund: {
      title: 'Issue Refund',
      pageTitle: 'Issue Refund',
      amount: 'Refund Amount',
      button: 'Issue Refund',
      sendButton: 'Send Refund',
      success: 'Refund Issued Successfully',
      successMessage: 'Refund has been issued to',
      message: 'has issued you a {{credit_amount}} refund',
      messageTitle: 'You received a refund!',
      currentBalance: 'Current Credit Balance',
      step1Title: 'Refund Details',
      step3Title: 'Review Refund',
    },
  };
  return texts[type]?.[key] || texts.credit[key];
};

const IssueCredit = ({ onSuccess, type = 'credit' }) => {
  const history = useHistory();
  const location = useLocation();
  const { customerId } = useParams();
  const { state } = useContext(AppContext);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [currentStep, setCurrentStep] = useState(1);
  const [toasterData, setToasterData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState(null);

  // Determine route prefix based on current path
  const { pathname } = window.location;
  const isTrackAdmin = pathname.includes('/admin-track');
  const isEmployeeAdmin = pathname.includes('/admin-employee');
  const isRegularAdmin = pathname.includes('/admin/');

  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const defaultReturnPath =
    isTrackAdmin || isEmployeeAdmin
      ? `${routePrefix}/customers`
      : `${routePrefix}/users`;

  // Get return path from URL params, fallback to default
  const searchParams = new URLSearchParams(location.search);
  const returnTo = searchParams.get('returnTo') || defaultReturnPath;
  const customersPath = returnTo;

  // Get account ID - different logic for admin vs track/employee
  let accountId = null;

  if (isRegularAdmin) {
    // In regular admin context, use the selectedAccountId from state
    accountId = selectedAccountId;
  } else {
    // In track/employee context, get from context or localStorage
    if (state && state.account) {
      accountId = state.account.id;
    } else {
      // Fallback to localStorage if context doesn't have it
      try {
        const accountData = localStorage.getItem('selected_account');
        if (accountData) {
          const account = JSON.parse(accountData);
          accountId = account.id;
        }
      } catch (error) {
        // Error reading account from localStorage
      }
    }
  }

  // Credit data state
  const [creditData, setCreditData] = useState({
    customerId: customerId,
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    currentBalance: 0,
    amount: '',
    expiration: null, // Empty by default - no expiration date
    reason: '',
    sendMessage: true, // Default to true - notify customer by default
    messageChannel: 'SMS',
    messageContent:
      type === 'credit'
        ? `Hi {{first_name}}!\n\nYou have been issued a credit for {{credit_amount}} by {{track_name}}.\n\nYou'll automatically be prompted to use the credit when checking out for your next purchase at {{track_name}}\n\nYou'll see Your Credit in the App in the Payments Section.\n\nThanks for using the ${getAppName()} App! 🏁`
        : `Hi {{first_name}},\n\n{{track_name}} issued you a {{credit_amount}} refund to your original payment method.\n\nPlease Allow 3-5 business days to process.\n\nThanks for using the ${getAppName()} App 🏁`,
    messageTitle: getAdjustmentText(type, 'messageTitle'), // For push notifications
    scheduledDateTime: null,
  });

  // Fetch user data - use different query based on context
  // For regular admin: use getUser (no account_id required)
  const {
    data: userData,
    loading: userLoading,
    error: userError,
  } = useQuery(GET_USER, {
    variables: {
      id: parseInt(customerId),
    },
    skip: !customerId || !isRegularAdmin,
  });

  // Fetch all accounts for the dropdown in admin context
  const {
    data: accountsData,
    loading: accountsLoading,
    error: accountsError,
  } = useQuery(GET_ACCOUNTS, {
    variables: {
      getAccountsInput: {},
    },
    skip: !isRegularAdmin,
    fetchPolicy: 'cache-and-network',
  });

  // For track/employee admin: use GetCustomer (requires account_id)
  const {
    data: customerData,
    loading: customerLoading,
    error: customerError,
  } = useQuery(GetCustomer, {
    variables: {
      input: {
        user_id: parseInt(customerId),
        account_id: accountId ? parseInt(accountId) : null,
      },
    },
    skip: !customerId || !accountId || isRegularAdmin,
  });

  // Fetch current credit balance using GraphQL
  const { data: balanceData, error: balanceError } = useQuery(
    GetUserOpenCreditsTotal,
    {
      variables: {
        input: {
          user_id: parseInt(customerId),
          account_id: accountId ? parseInt(accountId) : null,
        },
      },
      skip: !customerId || !accountId,
    }
  );

  // Update credit data when user/customer data is fetched
  useEffect(() => {
    let user = null;

    // Get user from the appropriate query
    if (isRegularAdmin && userData && userData.getUser) {
      user = userData.getUser;
    } else if (customerData && customerData.getCustomer) {
      user = customerData.getCustomer;
    }

    if (user) {
      // Format phone number without the +1 prefix
      let formattedPhone =
        formatPhoneNumber(user.cellphone) || user.cellphone || '';
      // Remove the +1 prefix if present to match desired format
      if (formattedPhone.startsWith('+1 ')) {
        formattedPhone = formattedPhone.substring(3);
      }

      setCreditData((prev) => ({
        ...prev,
        customerName: `${user.first_name} ${user.last_name}`.trim(),
        customerEmail: user.email || '',
        customerPhone: formattedPhone,
      }));
    }
  }, [customerData, userData, isRegularAdmin]);

  // Compute track name based on context
  const trackName = React.useMemo(() => {
    if (
      isRegularAdmin &&
      selectedAccountId &&
      accountsData?.getAccounts?.results
    ) {
      const selectedAccount = accountsData.getAccounts.results.find(
        (acc) => acc.id === selectedAccountId
      );
      if (selectedAccount) {
        return selectedAccount.business_dba || 'Your Track';
      }
      return 'Your Track';
    } else if (!isRegularAdmin) {
      // Track/Employee admin context
      if (state?.account?.business_dba) {
        return state.account.business_dba;
      } else if (state?.account?.business_name) {
        return state.account.business_name;
      } else {
        // Fallback to localStorage
        try {
          const accountData = localStorage.getItem('selected_account');
          if (accountData) {
            const account = JSON.parse(accountData);
            return (
              account.business_dba || account.business_name || 'Your Track'
            );
          }
        } catch (error) {
          // Error reading account from localStorage
        }
      }
    }
    return 'Your Track';
  }, [isRegularAdmin, selectedAccountId, accountsData, state]);

  // Update message content when account/track name changes
  useEffect(() => {
    if (isRegularAdmin && selectedAccountId && trackName !== 'Your Track') {
      // Update the message content to use the selected track name
      setCreditData((prev) => ({
        ...prev,
        messageContent:
          type === 'credit'
            ? `Hi {{first_name}}!\n\nYou have been issued a credit for {{credit_amount}} by ${trackName}.\n\nYou'll automatically be prompted to use the credit when checking out for your next purchase at ${trackName}\n\nYou'll see Your Credit in the App in the Payments Section.\n\nThanks for using the ${getAppName()} App! 🏁`
            : `Hi {{first_name}},\n\n${trackName} issued you a {{credit_amount}} refund to your original payment method.\n\nPlease Allow 3-5 business days to process.\n\nThanks for using the ${getAppName()} App 🏁`,
      }));
    }
  }, [selectedAccountId, trackName, isRegularAdmin, type]);

  // Update credit balance when fetched
  useEffect(() => {
    if (
      balanceData &&
      typeof balanceData.getUserOpenCreditsTotal !== 'undefined'
    ) {
      setCreditData((prev) => ({
        ...prev,
        currentBalance: parseFloat(balanceData.getUserOpenCreditsTotal) || 0,
      }));
    }
  }, [balanceData]);

  const handleBack = () => {
    if (currentStep === 1) {
      history.push(customersPath);
    } else {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleNext = () => {
    if (currentStep === 1) {
      // Validate account selection in admin context
      if (isRegularAdmin && !selectedAccountId) {
        setToasterData({
          title: 'Required Field Missing',
          message: 'Please select an account to issue the credit to.',
          type: 'error',
        });
        return;
      }

      // Validate amount field (required)
      if (!creditData.amount || creditData.amount.toString().trim() === '') {
        setToasterData({
          title: 'Required Field Missing',
          message: 'Amount is required. Please enter a credit amount.',
          type: 'error',
        });
        return;
      }

      // Validate amount is a valid number greater than 0
      const parsedAmount = parseFloat(creditData.amount);
      if (isNaN(parsedAmount) || parsedAmount <= 0) {
        setToasterData({
          title: 'Invalid Amount',
          message: 'Please enter a valid credit amount greater than $0.00',
          type: 'error',
        });
        return;
      }

      // If notifications are enabled, validate message content
      if (creditData.sendMessage) {
        // Check if message content exists
        if (
          !creditData.messageContent ||
          creditData.messageContent.trim() === ''
        ) {
          setToasterData({
            title: 'Required Field Missing',
            message:
              'Message content is required when notifications are enabled',
            type: 'error',
          });
          return;
        }
      }

      // Go directly to review step (step 2)
      setCurrentStep(2);
    }
  };

  // Initialize the mutation
  const [createCreditMutation] = useMutation(CREATE_CREDIT_V2);

  const handleIssueCredit = async () => {
    // Prevent multiple clicks
    if (isProcessing) return;

    try {
      setIsProcessing(true);

      // Show loading state
      setToasterData({
        title: 'Processing',
        message: `Issuing ${type}...`,
        type: 'info',
      });

      // Prepare the input for the mutation (trackName is from useMemo above)
      const input = {
        user_id: parseInt(customerId),
        account_id: parseInt(accountId),
        credit_amount: parseFloat(creditData.amount),
      };

      // Add reason if provided
      if (creditData.reason && creditData.reason.trim()) {
        input.reason = creditData.reason.trim();
      }

      // Add message and templateContext if sending notification
      if (creditData.sendMessage && creditData.messageContent) {
        // Send raw template message
        input.message = creditData.messageContent;

        // Add template context for static values
        input.templateContext = {
          credit_amount: `$${parseFloat(creditData.amount).toFixed(2)}`,
          track_name: trackName,
        };

        // Add refund-specific context if this is a refund
        if (type === 'refund') {
          input.templateContext.refund_amount =
            input.templateContext.credit_amount;
        }
      }

      // Execute the mutation
      const { data } = await createCreditMutation({
        variables: { input },
      });

      if (data?.createCreditV2?.id) {
        // Store success message in sessionStorage for the Customers page to display
        const successMessage = creditData.sendMessage
          ? `Successfully issued $${creditData.amount} ${type} to ${creditData.customerName}. Notification sent.`
          : `Successfully issued $${creditData.amount} ${type} to ${creditData.customerName}.`;

        sessionStorage.setItem('creditSuccessMessage', successMessage);

        if (onSuccess) {
          onSuccess({
            customerName: creditData.customerName,
            amount: creditData.amount,
            messageIncluded: creditData.sendMessage,
          });
        }

        // Redirect back to customers/users page
        history.push(customersPath);
      }
    } catch (error) {
      setIsProcessing(false);
      setToasterData({
        title: 'Error',
        message: error.message || 'Failed to issue credit. Please try again.',
        type: 'error',
      });
    }
  };

  const getStepComponent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
          >
            <Step1CreditDetails
              creditData={creditData}
              setCreditData={setCreditData}
              type={type}
              isRegularAdmin={isRegularAdmin}
              accounts={accountsData?.getAccounts?.results || []}
              selectedAccountId={selectedAccountId}
              onAccountChange={setSelectedAccountId}
            />
            {creditData.sendMessage && (
              <Step2MessageComposer
                creditData={creditData}
                setCreditData={setCreditData}
                trackName={trackName}
                type={type}
              />
            )}
          </div>
        );
      case 2:
        return (
          <Step3Review
            creditData={creditData}
            trackName={trackName}
            type={type}
          />
        );
      default:
        return null;
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return getAdjustmentText(type, 'title');
      case 2:
        return 'Review & Confirm';
      default:
        return '';
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        // Check if amount is valid
        const hasValidAmount =
          creditData.amount &&
          creditData.amount.trim() !== '' &&
          !isNaN(parseFloat(creditData.amount)) &&
          parseFloat(creditData.amount) > 0;

        // If notifications are disabled, only amount is required
        if (!creditData.sendMessage) {
          return hasValidAmount;
        }

        // If notifications are enabled, check message requirements
        const hasMessageContent =
          creditData.messageContent && creditData.messageContent.trim() !== '';

        return hasValidAmount && hasMessageContent;
      case 2:
        return true;
      default:
        return false;
    }
  };

  const steps = [
    `${type === 'refund' ? 'Refund' : 'Credit'} Details & Message`,
    'Review',
  ];

  // Show loading state while fetching initial data (not balance, which refetches on account change)
  if (userLoading || customerLoading || accountsLoading) {
    return (
      <EnhancedThemeProvider>
        <PageContainer>
          <PageContent>
            <div
              style={{
                textAlign: 'center',
                padding: '40px 0',
                color: '#94A3B8',
              }}
            >
              Loading customer information...
            </div>
          </PageContent>
        </PageContainer>
      </EnhancedThemeProvider>
    );
  }

  // Show error state if user/customer or balance fetch failed
  if (userError || customerError || balanceError || accountsError) {
    return (
      <EnhancedThemeProvider>
        <PageContainer>
          <PageContent>
            <div
              style={{
                textAlign: 'center',
                padding: '40px 0',
                color: '#EF4444',
              }}
            >
              Error loading user data:{' '}
              {
                (userError || customerError || balanceError || accountsError)
                  .message
              }
            </div>
          </PageContent>
        </PageContainer>
      </EnhancedThemeProvider>
    );
  }

  return (
    <EnhancedThemeProvider>
      <PageContainer>
        <PageContent>
          <StepHeader
            currentStep={currentStep}
            steps={steps}
            backPath={customersPath}
            backLabel="Back"
            onBack={handleBack}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '30px',
            }}
          >
            <PageTitle>{getStepTitle()}</PageTitle>
          </div>
          {getStepComponent()}
          <FooterButtons>
            {currentStep === 1 && (
              <NextButton onClick={handleNext}>
                Review {type === 'refund' ? 'Refund' : 'Credit'}
                <svg
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  />
                </svg>
              </NextButton>
            )}
            {currentStep === 2 && (
              <SendButton
                onClick={handleIssueCredit}
                disabled={!canProceed() || isProcessing}
              >
                {isProcessing ? (
                  <>
                    <SpinnerIcon
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M11 6.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm-6 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm6-6.5a.5.5 0 0 1 .5.5V1h1V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z" />
                    </SpinnerIcon>
                    Processing...
                  </>
                ) : (
                  <>
                    <svg
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                      style={{ marginRight: '8px' }}
                    >
                      <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v5H0V4zm0 6v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2H0zm3 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
                    </svg>
                    {getAdjustmentText(type, 'button')}
                  </>
                )}
              </SendButton>
            )}
          </FooterButtons>
        </PageContent>
      </PageContainer>

      <Toaster
        isOpen={!!toasterData}
        onClose={() => setToasterData(null)}
        title={toasterData?.title || ''}
        message={toasterData?.message || ''}
        type={toasterData?.type || 'info'}
        duration={5000}
      />
    </EnhancedThemeProvider>
  );
};

IssueCredit.propTypes = {
  onSuccess: PropTypes.func,
  type: PropTypes.oneOf(['credit', 'refund']),
};

export default IssueCredit;
